import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { toGtmCartItems, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import BackButton from "~/components/new/BackButton";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import CartDrawer from "~/components/new/CartDrawer";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import { trpc } from "~/api/trpc";
import CountryCodeCombobox from "~/components/new/CountryCodeCombobox";
import { orderedCountries } from "~/helper/form/dialCodes";
import KaliaLogo from "~/components/new/KaliaLogo";
import posthog from "posthog-js";
import { ArrowRight } from "lucide-react";
import TopProgressBar from "~/components/new/TopProgressBar";

// All known dial codes, longest first so "+1264" is matched before "+1".
const KNOWN_DIAL_CODES = Array.from(
    new Set(orderedCountries.map((c) => c.dial_code)),
).sort((a, b) => b.length - a.length);

// Helper to parse phone number and extract dial code
const parsePhoneNumber = (phone: string | undefined): { dialCode: string; number: string } => {
    if (!phone) {
        return { dialCode: "+49", number: "" };
    }

    for (const code of KNOWN_DIAL_CODES) {
        if (phone.startsWith(code)) {
            return { dialCode: code, number: phone.slice(code.length).trim() };
        }
    }

    // If no known code found, default to +49 and return the whole number
    return { dialCode: "+49", number: phone.replace(/^\+/, "").trim() };
};

const mailRegex = /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
const phoneRegex = /^[\+]?[\d\s\(\)\-\.]{5,25}$/;

const renderCheckboxLabel = (text: string) => {
    return text.split(/(\{[^}]+\})/).map((part, index) => {
        const match = part.match(/\{([^|]+)\|([^}]+)\}/);
        if (match) {
            const [, linkText, link] = match;
            const href = link === 'AGB'
                ? 'https://kalialab.de/agb'
                : 'https://kalialab.de/datenschutzerklaerung/';
            return (
                <a
                    key={index}
                    href={href}
                    className="text-burgundy underline hover:text-burgundy/70"
                    target='_blank'
                >
                    {linkText}
                </a>
            );
        }
        return part;
    });
};

const FixedBottomBar = () => {
    const {
        selectedTimeslot,
        cartItems,
        firstName,
        lastName,
        email,
        phoneDialCode,
        phone,
        agreeTermsConditionsAndPrivacyPolicy
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            phoneDialCode: state.phoneDialCode,
            phone: state.phone,
            agreeTermsConditionsAndPrivacyPolicy: state.agreeTermsConditionsAndPrivacyPolicy,
        }))
    );

    const [isCartOpen, setIsCartOpen] = useState(false);

    const navigate = useNavigate();

    const { language } = useLanguageStore();

    // CMS Config for button text
    const { data: enterDetailsCmsData } = trpc.publicBookingFunnel.getEnterDetailsConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const enterDetailsCms = enterDetailsCmsData?.config;

    const isFormValid = () => {
        return selectedTimeslot &&
            firstName.length >= 2 &&
            lastName.length >= 2 &&
            mailRegex.test(email) &&
            phoneRegex.test(phone) &&
            agreeTermsConditionsAndPrivacyPolicy;
    };

    const handleClick = () => {
        if (!isFormValid()) {
            return;
        }
        try {
            posthog.identify(email.trim().toLowerCase(), {
                name: `${firstName} ${lastName}`,
                email: email.trim().toLowerCase(),
                phone: phoneDialCode + phone.replaceAll(" ", "").toLowerCase(),
            });
        } catch (e) { }
        navigate("/review-booking");
    };

    const continueButtonText = language === Language.GERMAN
        ? (enterDetailsCms?.continueButtonTextDe ?? 'WEITER')
        : (enterDetailsCms?.continueButtonTextEn ?? 'CONTINUE');

    return (
        <>
            <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
                <PrimaryActionButton
                    onClick={handleClick}
                    label={continueButtonText.toUpperCase()}
                    disabled={!isFormValid()}
                    rightIcon={<ArrowRight />}
                />
            </div>
        </>
    );
};

const EnterDetails = () => {
    const {
        selectedStore,
        selectedTimeslot,
        cartItems,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        email,
        setEmail,
        phoneDialCode,
        setPhoneDialCode,
        phone,
        setPhone,
        wantsEarlierTimeslot,
        setWantsEarlierTimeslot,
        preferredContactTimes,
        setPreferredContactTimes,
        agreeTermsConditionsAndPrivacyPolicy,
        setAgreeTermsConditionsAndPrivacyPolicy,
        receiveNewsletter,
        setReceiveNewsletter,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            firstName: state.firstName,
            setFirstName: state.setFirstName,
            lastName: state.lastName,
            setLastName: state.setLastName,
            email: state.email,
            setEmail: state.setEmail,
            phoneDialCode: state.phoneDialCode,
            setPhoneDialCode: state.setPhoneDialCode,
            phone: state.phone,
            setPhone: state.setPhone,
            wantsEarlierTimeslot: state.wantsEarlierTimeslot,
            setWantsEarlierTimeslot: state.setWantsEarlierTimeslot,
            preferredContactTimes: state.preferredContactTimes,
            setPreferredContactTimes: state.setPreferredContactTimes,
            agreeTermsConditionsAndPrivacyPolicy: state.agreeTermsConditionsAndPrivacyPolicy,
            setAgreeTermsConditionsAndPrivacyPolicy: state.setAgreeTermsConditionsAndPrivacyPolicy,
            receiveNewsletter: state.receiveNewsletter,
            setReceiveNewsletter: state.setReceiveNewsletter,
        }))
    );

    const { sendEvent } = useGTM();
    const navigate = useNavigate();

    const { t, language } = useLanguageStore();
    const { customer, isAuthenticated } = useCustomerAuthStore();
    
    // Track if we've already pre-filled from auth
    const hasPrefilledRef = useRef(false);

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getEnterDetailsConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const cmsConfig = cmsConfigData?.config;

    // Helper function for CMS text
    const getCmsText = (fieldDe: string | undefined, fieldEn: string | undefined, fallbackDe: string, fallbackEn: string) => {
        return language === Language.GERMAN ? (fieldDe ?? fallbackDe) : (fieldEn ?? fallbackEn);
    };

    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });

    const validateField = (field: string, value: string) => {
        let error = '';

        switch (field) {
            case 'firstName':
                if (!value.trim()) {
                    error = t('firstNameRequired');
                } else if (value.length < 2) {
                    error = t('firstNameHelpMessage');
                }
                break;
            case 'lastName':
                if (!value.trim()) {
                    error = t('lastNameRequired');
                } else if (value.length < 2) {
                    error = t('lastNameHelpMessage');
                }
                break;
            case 'email':
                if (!value.trim()) {
                    error = t('emailAddressRequired');
                } else if (!mailRegex.test(value)) {
                    error = t('emailAddressHelpMessage');
                }
                break;
            case 'phone':
                if (!value.trim()) {
                    error = t('phoneNumberRequired');
                } else if (!phoneRegex.test(value)) {
                    error = t('phoneNumberHelpMessage');
                }
                break;
        }

        setErrors(prev => ({ ...prev, [field]: error }));
    };

    useEffect(() => {
        window.scroll(0, 0);
        sendEvent(GTMEvent.BOOKING_FUNNEL_APPOINTMENT_SELECTED, {
            appointment: {
                datetime: selectedTimeslot?.toUTC().toISOString(),
                notify_about_earlier_possible_appointment: false,
            },
            cart_items: toGtmCartItems(cartItems),
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: null,
                phone: null,
            },
        });
    }, []);

    useEffect(() => {
        if (!selectedStore) {
            navigate("/");
        }
    }, [selectedStore]);

    // Pre-fill form with customer data if authenticated and fields are empty
    useEffect(() => {
        if (hasPrefilledRef.current) {
            return;
        }

        if (isAuthenticated() && customer) {
            // Only pre-fill if fields are empty (don't overwrite user input)
            if (!firstName && customer.firstName) {
                setFirstName(customer.firstName);
            }
            if (!lastName && customer.lastName) {
                setLastName(customer.lastName);
            }
            if (!email && customer.email) {
                setEmail(customer.email);
            }
            if (!phone && customer.phone) {
                const parsed = parsePhoneNumber(customer.phone);
                setPhoneDialCode(parsed.dialCode);
                setPhone(parsed.number);
            }
            hasPrefilledRef.current = true;
        }
    }, [customer, isAuthenticated]);

    return (
        <>
            <TopProgressBar percentageFilled={100} displayString={language === Language.GERMAN ? (cmsConfig?.progressDisplayDe ?? 'SCHRITT 5 / 5') : (cmsConfig?.progressDisplayEn ?? 'STEP 5 / 5')} onClickBackButton={() => navigate("/find-time")} />
            <div className='flex flex-col gap-6 min-h-screen px-4 pt-8 pb-64'>
                <FixedBottomBar />

                <div className="relative w-full bg-white p-4 rounded-[5px] flex flex-col gap-6 drop-shadow-md">
                    <div className='flex flex-col gap-1 text-center'>
                        <span className='text-xl font-medium text-burgundy'>
                            {getCmsText(cmsConfig?.pageTitleDe, cmsConfig?.pageTitleEn, 'Deine Daten', 'Your details')}
                        </span>
                        <span className='text-sm text-burgundy/70'>
                            {getCmsText(cmsConfig?.pageSubtitleDe, cmsConfig?.pageSubtitleEn, 'Gib deine persönliche E-Mail-Adresse ein, um mit der Buchung fortzufahren', 'Enter your personal e-mail address to proceed with booking')}
                        </span>
                    </div>

                    {/* Name Fields */}
                    <div className='w-full flex gap-4'>
                        {/* First Name Field */}
                        <div className='flex-1 flex flex-col gap-2'>
                            <Input
                                id="firstName"
                                type="text"
                                placeholder={getCmsText(cmsConfig?.firstNamePlaceholderDe, cmsConfig?.firstNamePlaceholderEn, 'Vorname', 'First name')}
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    validateField('firstName', e.target.value);
                                }}
                                className={`m-0 p-0 shadow-none h-10 border-0 border-b rounded-none bg-transparent font-light text-burgundy placeholder:text-burgundy/50 focus-visible:ring-0 ${errors.firstName
                                    ? 'border-red-400'
                                    : 'border-burgundy focus-visible:border-burgundy'
                                    }`}
                            />
                            {errors.firstName && (
                                <span className="text-sm text-red-600">{errors.firstName}</span>
                            )}
                        </div>

                        {/* Last Name Field */}
                        <div className='flex-1 flex flex-col gap-2'>
                            <Input
                                id="lastName"
                                type="text"
                                placeholder={getCmsText(cmsConfig?.lastNamePlaceholderDe, cmsConfig?.lastNamePlaceholderEn, 'Nachname', 'Last name')}
                                value={lastName}
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    validateField('lastName', e.target.value);
                                }}
                                className={`m-0 p-0 shadow-none h-10 border-0 border-b rounded-none bg-transparent font-light text-burgundy placeholder:text-burgundy/50 focus-visible:ring-0 ${errors.lastName
                                    ? 'border-red-400'
                                    : 'border-burgundy focus-visible:border-burgundy'
                                    }`}
                            />
                            {errors.lastName && (
                                <span className="text-sm text-red-600">{errors.lastName}</span>
                            )}
                        </div>
                    </div>

                    {/* Email Field */}
                    <div className='w-full flex flex-col gap-2'>
                        <Input
                            id="email"
                            type="email"
                            placeholder={getCmsText(cmsConfig?.emailPlaceholderDe, cmsConfig?.emailPlaceholderEn, 'E-Mail Adresse', 'Email address')}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                validateField('email', e.target.value);
                            }}
                            className={`m-0 p-0 shadow-none h-10 border-0 border-b rounded-none bg-transparent font-light text-burgundy placeholder:text-burgundy/50 focus-visible:ring-0 ${errors.email
                                ? 'border-red-400'
                                : 'border-burgundy focus-visible:border-burgundy'
                                }`}
                        />
                        {errors.email && (
                            <span className="text-sm text-red-600">{errors.email}</span>
                        )}
                    </div>

                    {/* Phone Number Field */}
                    <div className='w-full flex flex-col gap-2'>
                        <div className="flex gap-0 border-b border-burgundy">
                            <CountryCodeCombobox
                                value={
                                    orderedCountries.find((c) => c.dial_code === phoneDialCode)?.code ?? "DE"
                                }
                                onSelect={(country) => setPhoneDialCode(country.dial_code)}
                                searchPlaceholder={getCmsText(undefined, undefined, 'Land suchen', 'Search country')}
                                emptyText={getCmsText(undefined, undefined, 'Kein Land gefunden', 'No country found')}
                            />
                            <Input
                                id="phone"
                                type="tel"
                                placeholder={getCmsText(cmsConfig?.phonePlaceholderDe, cmsConfig?.phonePlaceholderEn, 'Telefonnummer', 'Phone number')}
                                value={phone}
                                onChange={(e) => {
                                    setPhone(e.target.value);
                                    validateField('phone', e.target.value);
                                }}
                                className="m-0 p-0 shadow-none h-10 border-0 rounded-none bg-transparent font-light text-burgundy placeholder:text-burgundy/50 focus-visible:ring-0"
                            />
                        </div>
                        {errors.phone && (
                            <span className="text-sm text-red-600">{errors.phone}</span>
                        )}
                    </div>

                    {/* Terms and Conditions */}
                    <div className='w-full flex flex-col gap-3'>
                        <div className="flex items-center gap-4">
                            <Checkbox
                                id="terms"
                                checked={agreeTermsConditionsAndPrivacyPolicy}
                                onCheckedChange={setAgreeTermsConditionsAndPrivacyPolicy}
                                className="shadow-none border-burgundy/50 data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy"
                            />
                            <Label htmlFor="terms" className="text-sm text-burgundy/80 leading-relaxed font-light">
                                {renderCheckboxLabel(getCmsText(cmsConfig?.termsCheckboxTextDe, cmsConfig?.termsCheckboxTextEn, 'Ich akzeptiere die AGB und Datenschutzerklärung', 'I accept the terms and privacy policy'))}
                            </Label>
                        </div>
                    </div>

                    {/* Earlier Timeslot Checkbox */}
                    <div className='w-full flex flex-col gap-3'>
                        <div className="flex items-center gap-4">
                            <Checkbox
                                id="earlier-timeslot"
                                checked={wantsEarlierTimeslot}
                                onCheckedChange={setWantsEarlierTimeslot}
                                className="shadow-none border-burgundy/50 data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy"
                            />
                            <Label htmlFor="earlier-timeslot" className="text-sm text-burgundy/80 leading-relaxed font-normal">
                                {getCmsText(cmsConfig?.wantEarlierTimeslotLabelDe, cmsConfig?.wantEarlierTimeslotLabelEn, 'Ich möchte einen früheren Termin, falls einer frei wird', "I'd like an earlier appointment if one becomes available")}
                            </Label>
                        </div>

                        {/* Preferred Contact Times - shown when wantsEarlierTimeslot is true */}
                        {wantsEarlierTimeslot && (
                            <div className="w-full flex flex-col gap-2 ml-8">
                                <Label className="text-sm font-light text-burgundy">
                                    {getCmsText(cmsConfig?.preferredContactTimesLabelDe, cmsConfig?.preferredContactTimesLabelEn, 'Bevorzugte Kontaktzeiten', 'Preferred contact times')}
                                </Label>
                                <div className="flex flex-wrap gap-3">
                                    {[
                                        { id: 'Morning (8-12)', label: getCmsText(cmsConfig?.contactTimeMorningDe, cmsConfig?.contactTimeMorningEn, 'Vormittag (8-12)', 'Morning (8-12)') },
                                        { id: 'Afternoon (12-16)', label: getCmsText(cmsConfig?.contactTimeAfternoonDe, cmsConfig?.contactTimeAfternoonEn, 'Nachmittag (12-16)', 'Afternoon (12-16)') },
                                        { id: 'Evening (16-22)', label: getCmsText(cmsConfig?.contactTimeEveningDe, cmsConfig?.contactTimeEveningEn, 'Abend (16-22)', 'Evening (16-22)') }
                                    ].map((timeSlot) => (
                                        <div key={timeSlot.id} className="flex items-center gap-2">
                                            <Checkbox
                                                id={`contact-time-${timeSlot.id}`}
                                                checked={preferredContactTimes.includes(timeSlot.id)}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        setPreferredContactTimes([...preferredContactTimes, timeSlot.id]);
                                                    } else {
                                                        setPreferredContactTimes(preferredContactTimes.filter(t => t !== timeSlot.id));
                                                    }
                                                }}
                                                className="shadow-none border-burgundy/50 data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy"
                                            />
                                            <Label
                                                htmlFor={`contact-time-${timeSlot.id}`}
                                                className="text-sm text-burgundy/80 cursor-pointer"
                                            >
                                                {timeSlot.label}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Newsletter opt-out. Unchecked means the customer keeps the newsletter. */}
                    <div className='w-full flex flex-col gap-3'>
                        <div className="flex items-center gap-4">
                            <Checkbox
                                id="newsletter"
                                checked={receiveNewsletter === false}
                                onCheckedChange={(checked) => setReceiveNewsletter(checked !== true)}
                                className="shadow-none border-burgundy/50 data-[state=checked]:bg-burgundy data-[state=checked]:border-burgundy"
                            />
                            <Label htmlFor="newsletter" className="text-sm text-burgundy/80 leading-relaxed font-light">
                                {renderCheckboxLabel(getCmsText(
                                    cmsConfig?.newsletterCheckboxTextDe,
                                    cmsConfig?.newsletterCheckboxTextEn,
                                    'Ich möchte keinen Newsletter erhalten.',
                                    'I do not want to receive the newsletter.',
                                ))}
                            </Label>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EnterDetails;